import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import axiosInstance from '../axiosInstance';
import VideoCallComponent from '../components/VideoCallComponent';

const PatientJoinPage = () => {
  // Soporta dos rutas:
  // - /telemedicine/room/:roomId       -> usa verify-session
  // - /telemedicine/join/:token        -> resuelve token a room_id
  const { roomId: routeRoomId, token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionValid, setSessionValid] = useState(false);
  const [doctorName, setDoctorName] = useState('Doctor');
  const [patientName, setPatientName] = useState('Paciente');
  const [roomId, setRoomId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const verifyWithRoom = async (rid) => {
      try {
        setLoading(true);
        const resp = await axiosInstance.get(`/telemedicine/verify-session/${rid}/`);
        if (!isMounted) return;
        if (resp.data?.valid) {
          setSessionValid(true);
          setRoomId(rid);
          setDoctorName(resp.data.doctor_name || 'Doctor');
          setPatientName(resp.data.patient_name || 'Paciente');
        } else {
          setError('La sesión no es válida o ha expirado.');
        }
      } catch (err) {
        console.error('Error al verificar la sesión:', err);
        setError('Error al verificar la sesión. Por favor, verifique el enlace o contacte al médico.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const resolveToken = async (tk) => {
      try {
        setLoading(true);
        // Resuelve token -> room_id, names
        const resp = await axiosInstance.get(`/telemedicine/join/${tk}/`);
        if (!isMounted) return;
        const rid = resp.data?.room_id;
        if (rid) {
          setSessionValid(true);
          setRoomId(rid);
          setDoctorName(resp.data?.doctor_name || 'Doctor');
          setPatientName(resp.data?.patient_name || 'Paciente');
        } else {
          setError('Token inválido o sin room asignada.');
        }
      } catch (err) {
        console.error('Error al resolver token:', err);
        setError('Invitación inválida o expirada. Solicite un nuevo enlace al médico.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (token) {
      resolveToken(token);
    } else if (routeRoomId) {
      verifyWithRoom(routeRoomId);
    } else {
      setError('Enlace inválido. Falta roomId o token.');
      setLoading(false);
    }

    return () => { isMounted = false; };
  }, [routeRoomId, token]);

  const handleEndCall = () => {
    // Siempre redirigir al finalizar
    navigate('/thank-you');
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default'
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verificando sesión...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        p: 3
      }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            maxWidth: 500,
            textAlign: 'center',
            bgcolor: 'background.paper'
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Error de Conexión
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.close()}
          >
            Cerrar Ventana
          </Button>
        </Paper>
      </Box>
    );
  }

  if (sessionValid && roomId) {
    return (
      <Box sx={{ width: '100%', height: '100%', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
        <VideoCallComponent
          roomId={roomId}
          patientName={patientName}
          doctorName={doctorName}
          onEndCall={handleEndCall}
          isPatient={true}  // ¡Importante!
        />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      bgcolor: 'background.default'
    }}>
      <Typography variant="h6">
        Estado de sesión desconocido. Por favor, recargue la página.
      </Typography>
    </Box>
  );
};

export default PatientJoinPage;
